import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { mapBackendError } from "@/i18n/errorMap";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Lock, Mail } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await login(email, password);
      toast.success(t("login.title"));
      nav(data?.is_super_admin ? "/admin" : "/");
    } catch (err) {
      setError(mapBackendError(err.response?.data?.detail, t) || err.message);
      setBusy(false);
    }
  };

  return (
    <AuthShell
      step={t("auth.stepLogin")}
      title={t("login.title")}
      subtitle={t("login.subtitle")}
    >
      <form onSubmit={submit} data-testid="login-form" className="space-y-5">
        <div>
          <Label htmlFor="email" className="font-geist-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{t("login.emailLabel")}</Label>
          <div className="relative mt-2">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder={t("register.emailPlaceholder")}
              className="pl-10 h-11 rounded-sm bg-white/[0.04] border-white/[0.1] text-white placeholder:text-white/25 font-geist focus-visible:ring-[#4D8AFF] focus-visible:border-[#4D8AFF]/50"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="font-geist-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{t("login.passwordLabel")}</Label>
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="font-geist-mono text-[10px] uppercase tracking-wider text-[#4D8AFF] hover:text-white transition-colors"
            >{t("login.forgot")}</Link>
          </div>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <PasswordInput
              id="password"
              data-testid="login-password-input"
              toggleTestId="login-password-toggle"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              className="pl-10 h-11 rounded-sm bg-white/[0.04] border-white/[0.1] text-white placeholder:text-white/25 font-geist focus-visible:ring-[#4D8AFF] focus-visible:border-[#4D8AFF]/50"
            />
          </div>
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="font-geist-mono text-[11px] text-red-300 border border-red-400/30 bg-red-500/10 rounded-sm px-3 py-2"
          >{error}</div>
        )}

        <Button
          data-testid="login-submit-btn"
          type="submit"
          disabled={busy}
          className="w-full h-11 rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold tracking-tight group transition-colors"
        >
          {busy ? (
            <span className="font-geist-mono text-xs uppercase tracking-wider">{t("login.submitting")}</span>
          ) : (
            <span className="flex items-center gap-2">
              {t("login.submit")}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          )}
        </Button>
      </form>

      <div className="mt-6 pt-6 border-t border-white/[0.08] flex items-center justify-between font-geist-mono text-[11px] text-white/40">
        <span>{t("login.noAccount")}</span>
        <Link
          to="/register"
          data-testid="go-to-register-link"
          className="text-[#4D8AFF] hover:text-white transition-colors uppercase tracking-wider"
        >{t("login.toRegister")}</Link>
      </div>
    </AuthShell>
  );
}
